import React, { useState, useRef, useEffect } from 'react';
import {
  PlayIcon,
  PauseIcon,
  StarIcon,
  TrashIcon,
  PlusIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import { SavedVoiceSample } from '../../types';
import {
  getSavedVoiceSamples,
  setDefaultVoiceSample,
  deleteSavedVoiceSample,
} from '../../lib/characterVoiceService';

interface SavedVoicesPanelProps {
  characterId: string;
  userId: string;
  selectedSampleId?: string | null;
  onSelectSample: (sample: SavedVoiceSample | null) => void;
  onCreateNew: () => void;
  refreshTrigger?: number;
  className?: string;
  integrated?: boolean; // Borderless mode for integrated layouts
}

export const SavedVoicesPanel: React.FC<SavedVoicesPanelProps> = ({
  characterId,
  userId,
  selectedSampleId,
  onSelectSample,
  onCreateNew,
  refreshTrigger = 0,
  className = '',
  integrated = false,
}) => {
  const [samples, setSamples] = useState<SavedVoiceSample[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Load saved samples
  useEffect(() => {
    const loadSamples = async () => {
      setIsLoading(true);
      try {
        const data = await getSavedVoiceSamples(characterId);
        setSamples(data);
      } catch (error) {
        console.error('Failed to load saved voice samples:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadSamples();
  }, [characterId, refreshTrigger]);

  // Handle audio playback
  const handlePlay = (sample: SavedVoiceSample, e: React.MouseEvent) => {
    e.stopPropagation();
    if (playingId === sample.id) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.src = sample.audio_url;
        audioRef.current.play().catch(console.warn);
        setPlayingId(sample.id);
      }
    }
  };

  const handleAudioEnded = () => {
    setPlayingId(null);
  };

  // Handle make default
  const handleMakeDefault = async (sample: SavedVoiceSample, e: React.MouseEvent) => {
    e.stopPropagation();
    if (sample.is_default) return;

    setSettingDefaultId(sample.id);
    try {
      await setDefaultVoiceSample(sample.id, characterId);
      // Update local state
      setSamples(prev =>
        prev.map(s => ({
          ...s,
          is_default: s.id === sample.id,
        }))
      );
    } catch (error) {
      console.error('Failed to set default:', error);
    } finally {
      setSettingDefaultId(null);
    }
  };

  // Handle delete
  const handleDelete = async (sample: SavedVoiceSample, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Delete this saved voice?')) return;

    setDeletingId(sample.id);
    try {
      await deleteSavedVoiceSample(sample.id, characterId);
      setSamples(prev => prev.filter(s => s.id !== sample.id));
      if (selectedSampleId === sample.id) {
        onSelectSample(null);
      }
    } catch (error) {
      console.error('Failed to delete sample:', error);
    } finally {
      setDeletingId(null);
    }
  };

  // Truncate quote for display
  const truncateQuote = (quote: string, maxLength = 60) => {
    if (quote.length <= maxLength) return quote;
    return quote.substring(0, maxLength).trim() + '...';
  };

  if (isLoading) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center py-4">
          <CircleNotchIcon className="w-3.5 h-3.5 animate-spin text-slate-blue" />
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Hidden audio element for playback */}
      <audio ref={audioRef} onEnded={handleAudioEnded} />

      {/* Header - compact */}
      {samples.length > 0 && (
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-[10px] font-medium text-dust/40 uppercase tracking-wide">
            Saved
          </span>
          <span className="text-[9px] text-dust/20 bg-white/5 px-1.5 py-0.5 rounded">
            {samples.length}
          </span>
        </div>
      )}

      {/* Samples List - compact cards */}
      <div className="space-y-1.5">
        {samples.length === 0 ? (
          <p className="text-[10px] text-dust/25 text-center py-2">No saved voices</p>
        ) : (
          samples.map(sample => (
            <div
              key={sample.id}
              onClick={() => onSelectSample(sample)}
              className={`group relative p-2 rounded-lg cursor-pointer transition-all ${
                selectedSampleId === sample.id
                  ? 'bg-slate-blue/20 ring-1 ring-slate-blue/50'
                  : 'bg-white/[0.02] hover:bg-white/[0.05]'
              }`}
            >
              <div className="flex items-center gap-2">
                {/* Play Button - compact */}
                <button
                  onClick={(e) => handlePlay(sample, e)}
                  className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 transition-all ${
                    playingId === sample.id
                      ? 'bg-slate-blue text-white'
                      : 'bg-white/5 text-dust/40 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {playingId === sample.id ? (
                    <PauseIcon className="w-3 h-3" weight="fill" />
                  ) : (
                    <PlayIcon className="w-3 h-3" weight="fill" />
                  )}
                </button>

                {/* Content - minimal */}
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-dust/70 line-clamp-2 leading-snug">
                    {truncateQuote(sample.quote, 40)}
                  </p>
                </div>

                {/* Default indicator or actions */}
                <div className="shrink-0">
                  {sample.is_default ? (
                    <StarIcon className="w-3 h-3 text-slate-blue" weight="fill" />
                  ) : (
                    <div className={`flex gap-0.5 transition-opacity ${
                      selectedSampleId === sample.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    }`}>
                      <button
                        onClick={(e) => handleMakeDefault(sample, e)}
                        disabled={settingDefaultId === sample.id}
                        className="p-0.5 text-dust/25 hover:text-slate-blue transition-colors"
                        title="Set default"
                      >
                        {settingDefaultId === sample.id ? (
                          <CircleNotchIcon className="w-2.5 h-2.5 animate-spin" />
                        ) : (
                          <StarIcon className="w-2.5 h-2.5" />
                        )}
                      </button>
                      <button
                        onClick={(e) => handleDelete(sample, e)}
                        disabled={deletingId === sample.id}
                        className="p-0.5 text-dust/25 hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        {deletingId === sample.id ? (
                          <CircleNotchIcon className="w-2.5 h-2.5 animate-spin" />
                        ) : (
                          <TrashIcon className="w-2.5 h-2.5" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create New Button - compact */}
      <button
        onClick={onCreateNew}
        className={`w-full mt-2 flex items-center justify-center gap-1 py-1.5 rounded-md transition-all text-[10px] font-medium ${
          selectedSampleId
            ? 'bg-slate-blue text-white hover:bg-[#8583f9]'
            : 'text-dust/30 hover:text-dust/50 hover:bg-white/[0.03]'
        }`}
      >
        <PlusIcon className="w-3 h-3" />
        <span>Create New</span>
      </button>
    </div>
  );
};

export default SavedVoicesPanel;
